/**
 * `/agents/goals` — the "big delegator" MID recipe, landed once (RO-3-02, PRD
 * §11.3, §13 R5). Port of `excellent/app/agents/goals/{page,new/page}.tsx` +
 * `agents/goals/[id]/page.tsx`, which delegated to
 * `.../[sectionSlug]/_agents/views.tsx`'s `GoalsView`/`NewGoalView` (a
 * 449-LOC async-server-component + native-server-action pair). The DATA
 * assembly moved to `server/routes/agent-goals.ts`; the JSX below is that
 * same delegate, unchanged where `@excellent/ui` already owns the surface
 * (`GoalsLedger`, `PageHeader`, `FormField`, …).
 *
 * #44483 — toolbar is the ported `NewGoalDialog` (RO-3-00g cause-B). Agentic
 * scope keeps `allowStrategic={false}` so this ledger matches the agentic
 * branch; Work ▸ Goals / GoalsView use the dual-kind modal.
 */

import * as React from "react";
import { z } from "zod";
import { createRoute, redirect } from "@tanstack/react-router";
import {
  Button,
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  FormField,
  GoalsLedger,
  Input,
  PageHeader,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Textarea,
} from "@excellent/ui";
import { useApi, apiCall } from "@excellent/ui/api";
import { agentGoals, type AgentGoalCreateResult } from "@excellent/ui/api/contracts/agent-goals";
import { rootRoute } from "./__root";
import { routeSearchSchema } from "../shell/search";
import { NewGoalDialog } from "../shared/modes/new-goal-dialog";

// ── /agents/goals — the agentic-scoped ledger ────────────────────────────────

function AgentsGoalsIndex() {
  const { data, error, isLoading } = useApi(agentGoals.list, { query: {} });
  const { data: rolesData } = useApi(agentGoals.roles, {});
  const roles = (rolesData?.roles ?? []).map((r) => ({
    name: r.name,
    description: r.description ?? "",
  }));

  return (
    <div className="flex flex-col">
      <div className="px-6 pb-8 pt-8 flex flex-col gap-5">
        {isLoading && !data ? <p className="type-body text-muted-foreground">Loading…</p> : null}
        {error ? (
          <p className="type-body text-destructive">{error.message ?? String(error)}</p>
        ) : null}
        {data ? (
          <GoalsLedger
            goals={data.goals}
            basePath="/"
            toolbar={<NewGoalDialog roles={roles} allowStrategic={false} />}
          />
        ) : null}
      </div>
    </div>
  );
}

export const agentsGoalsRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/agents/goals",
  component: AgentsGoalsIndex,
});

// ── /agents/goals/new — the create form ──────────────────────────────────────

const newGoalSearchSchema = routeSearchSchema({
  error: z.string().optional(),
  title: z.string().optional(),
  role: z.string().optional(),
  // NOT `task` — the original Next page's `?task=<id>` (link the new goal to
  // its originating task) collides with the shell-wide `task` reserved for the
  // object peek (`TaskPeekHost` rewrites any `?task=` into `?peek=` on every
  // route). `routeSearchSchema` refuses to compile the collision (I9) — this
  // is that refusal, not a stylistic rename. `linkTask` carries the same value
  // over the wire as the original's `task` body field.
  linkTask: z.string().optional(),
  lane: z.string().optional(),
});

const GOAL_ERROR_LABELS: Record<string, string> = {
  "title-required": "Title is required.",
  "unknown-role": "Pick a role from the list — those are the only authored roles.",
  "project-not-found": "That project slug doesn't exist.",
};

function NewAgentsGoal() {
  const search = agentsGoalsNewRoute.useSearch();
  const navigate = agentsGoalsNewRoute.useNavigate();
  const { data: rolesData } = useApi(agentGoals.roles, {});
  const roles = rolesData?.roles ?? [];

  const [title, setTitle] = React.useState(search.title ?? "");
  const [outcome, setOutcome] = React.useState("");
  const [why, setWhy] = React.useState("");
  const [role, setRole] = React.useState(search.role ?? "shipper");
  const [description, setDescription] = React.useState("");
  const [cadence, setCadence] = React.useState("on-demand");
  const [project, setProject] = React.useState("");
  const [submitting, setSubmitting] = React.useState(false);
  const [error, setError] = React.useState<string | null>(
    search.error ? (GOAL_ERROR_LABELS[search.error] ?? search.error) : null,
  );

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    setSubmitting(true);
    setError(null);
    try {
      const result: AgentGoalCreateResult = await apiCall(agentGoals.create, {
        body: {
          title,
          outcome,
          why,
          role,
          cadence,
          project,
          description,
          task: search.linkTask,
          lane: search.lane,
        },
      });
      if (result.ok) {
        // `navigate`, not `redirect()` — the latter is a route-loader control
        // flow primitive (thrown from `beforeLoad`/`loader`); thrown from an
        // event handler it is just an uncaught exception.
        const dest = result.uid ? `/o/${result.uid}?from=agents` : "/agents/goals";
        void navigate({ to: dest });
        return;
      }
      setError(GOAL_ERROR_LABELS[result.reason] ?? result.detail ?? result.reason);
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div className="flex flex-col">
      <div className="px-6 py-8">
        <PageHeader
          className="mb-5"
          title="New goal"
          description="Durable intent for the agent system. The autopilot scheduler fires the named role on the cadence you set, scoped to this goal."
          toolbar={
            <Button
              variant="outline"
              onClick={() => void navigate({ to: "/agents/goals" })}
            >
              ← Goals
            </Button>
          }
        />

        <Card className="max-w-2xl">
          <CardHeader>
            <CardTitle className="type-title">Goal definition</CardTitle>
          </CardHeader>
          <CardContent>
            {error ? (
              <div className="mb-4 rounded border border-destructive/40 bg-destructive/5 px-3 py-2 type-body text-destructive">
                {error}
              </div>
            ) : null}
            <form onSubmit={submit} className="space-y-5">
              <FormField label="Title" required htmlFor="goal-title">
                <Input
                  id="goal-title"
                  value={title}
                  onChange={(e) => setTitle(e.target.value)}
                  placeholder="Clear evidence decisions weekly"
                  required
                  autoFocus
                />
              </FormField>

              <FormField
                label="Outcome"
                required
                htmlFor="goal-outcome"
                help="One sentence — what's true when this is done. You can add the metric / checklist finish line after creating."
              >
                <Textarea
                  id="goal-outcome"
                  value={outcome}
                  onChange={(e) => setOutcome(e.target.value)}
                  required
                  rows={2}
                  placeholder="New workspaces reach first real value in under 5 minutes."
                />
              </FormField>

              <FormField label="Why it matters (optional)" htmlFor="goal-why">
                <Textarea
                  id="goal-why"
                  value={why}
                  onChange={(e) => setWhy(e.target.value)}
                  rows={2}
                  placeholder="The stakes — what this unlocks, or the problem it solves."
                />
              </FormField>

              <FormField
                label="Role"
                help={roles.length === 0 ? undefined : "Only authored roles can run."}
                error={roles.length === 0 ? "No roles found in packages/agents/roles/." : undefined}
              >
                <Select value={role} onValueChange={setRole}>
                  <SelectTrigger className="mt-2 w-full">
                    <SelectValue placeholder="Pick a role" />
                  </SelectTrigger>
                  <SelectContent>
                    {roles.map((r) => (
                      <SelectItem key={r.name} value={r.name}>
                        {r.name} — {r.description.slice(0, 80)}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </FormField>

              <FormField label="Description (optional)" htmlFor="goal-description">
                <Textarea
                  id="goal-description"
                  value={description}
                  onChange={(e) => setDescription(e.target.value)}
                  rows={3}
                  placeholder="What success looks like, what's in/out of scope, and any constraints."
                />
              </FormField>

              <FormField
                label="Cadence"
                htmlFor="goal-cadence"
                help={
                  <>
                    Recognized values: <code>on-demand</code>, <code>every 5m</code>,{" "}
                    <code>every 1h</code>, <code>daily HH:MM</code>,{" "}
                    <code>weekdays HH:MM</code>. Setting a cadence does NOT auto-create
                    cron — the worker prints a <code>/schedule</code> recipe (per
                    CLAUDE.md).
                  </>
                }
              >
                <Input
                  id="goal-cadence"
                  value={cadence}
                  onChange={(e) => setCadence(e.target.value)}
                  placeholder="on-demand"
                />
              </FormField>

              <FormField
                label="Project (optional)"
                htmlFor="goal-project"
                help="Scopes the goal's tasks to this project."
              >
                <Input
                  id="goal-project"
                  value={project}
                  onChange={(e) => setProject(e.target.value)}
                  placeholder="project-slug"
                />
              </FormField>

              <div className="flex items-center justify-end gap-3 pt-2">
                <Button
                  variant="outline"
                  type="button"
                  onClick={() => void navigate({ to: "/agents/goals" })}
                >
                  Cancel
                </Button>
                <Button type="submit" disabled={submitting}>
                  {submitting ? "Creating…" : "Create goal"}
                </Button>
              </div>
            </form>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}

export const agentsGoalsNewRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/agents/goals/new",
  validateSearch: newGoalSearchSchema,
  component: NewAgentsGoal,
});

// ── /agents/goals/$id — legacy numeric-id compat redirect ────────────────────
// Port of `agents/goals/[id]/page.tsx` (RE-HOMED onto the canonical object
// page): resolve the numeric id to its uid, then redirect to `/o/<uid>`. A
// goal that no longer exists (bad id, or archived) falls back to the goals
// list rather than a bare 404 — there is nothing useful to show at this URL
// either way.

export const agentsGoalRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/agents/goals/$id",
  beforeLoad: async ({ params }) => {
    const id = Number(params.id);
    if (!Number.isFinite(id) || id <= 0) {
      throw redirect({ to: "/agents/goals", replace: true });
    }
    const result = await apiCall(agentGoals.resolve, { params: { id } });
    if (!result.uid) {
      throw redirect({ to: "/agents/goals", replace: true });
    }
    throw redirect({ to: `/o/${result.uid}?from=agents`, replace: true });
  },
});
